import { useState, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import type { QuestionAnswer } from './types'
import { TrendBadge } from './TrendBadge'
import { parseSimpleMarkdown } from '@/utils/markdown'

interface QuestionAnswerCardProps {
  qa: QuestionAnswer
  isLast?: boolean
  defaultOpen?: boolean
  /** Controlled mode: if provided, overrides internal state */
  isOpen?: boolean
  /** Controlled mode: called when user toggles the card */
  onToggle?: () => void
}

export function QuestionAnswerCard({
  qa,
  isLast = false,
  defaultOpen = true,
  isOpen: controlledIsOpen,
  onToggle,
}: QuestionAnswerCardProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen)

  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen

  // Sync internal state with controlled state when it changes
  useEffect(() => {
    if (controlledIsOpen !== undefined) {
      setInternalIsOpen(controlledIsOpen)
    }
  }, [controlledIsOpen])

  const handleToggle = () => {
    if (onToggle) {
      onToggle()
    } else {
      setInternalIsOpen(!internalIsOpen)
    }
  }
  const formattedAnswer = parseSimpleMarkdown(qa.answer)

  return (
    <article
      className={`${isLast ? 'pb-0' : 'pb-7 mb-7 border-b border-stone-100 dark:border-stone-800/60'}`}
    >
      {/* Question header - clickable to toggle */}
      <button
        onClick={handleToggle}
        className="w-full flex items-start justify-between gap-4 text-left group"
      >
        <h4 className="text-lg font-semibold text-stone-900 dark:text-stone-50 leading-snug tracking-tight group-hover:text-stone-700 dark:group-hover:text-stone-200 transition-colors">
          {qa.questionText}
        </h4>
        <ChevronDown
          className={`w-5 h-5 mt-1 flex-shrink-0 text-stone-400 dark:text-stone-500 transition-transform duration-200 ${
            isOpen ? 'rotate-0' : '-rotate-90'
          }`}
        />
      </button>

      {/* Collapsible content */}
      <div
        className={`grid transition-all duration-200 ease-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0 mt-0'
        }`}
      >
        <div className="overflow-hidden">
          {/* Answer */}
          <div
            className={`text-[15px] text-stone-600 dark:text-stone-400 leading-relaxed ${qa.trend ? 'mb-4' : ''}`}
            dangerouslySetInnerHTML={{ __html: formattedAnswer }}
          />

          {/* Trend badge */}
          {qa.trendLabel && qa.trendIcon && (
            <TrendBadge
              label={qa.trendLabel}
              icon={qa.trendIcon}
              explanation={qa.trendExplanation}
            />
          )}
        </div>
      </div>
    </article>
  )
}
